import { useState } from "react";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { submitLead } from "@/lib/api";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";
import { trackLead } from "@/lib/analytics";
import { toast } from "sonner";

const INPUT_CLS = "w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-clay";
const PERKS = [
  "Free 1:1 consultation",
  "Personalized program recommendation",
  "Live classes you can join this week",
];

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block eyebrow text-ink-muted mb-1.5">{label}{required && <span className="text-clay">*</span>}</span>
      {children}
    </label>
  );
}

export default function LeadFormSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ ...form, source: "home_form" });
      trackLead("home_form");
      toast.success("Thank you! We'll message you on WhatsApp shortly.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-form" className="section-pad">
      <div className="container-x">
        <div className="rounded-[2rem] md:rounded-[2.5rem] overflow-hidden border border-sand-300 grid lg:grid-cols-2 bg-white">
          <div className="relative p-8 md:p-14 bg-forest text-white">
            <p className="eyebrow text-clay-light">Get started</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl leading-tight">
              Chat with us. <em className="italic text-clay-light">No pressure.</em>
            </h2>
            <p className="mt-4 text-white/80 max-w-md">
              The fastest way? Message us directly on WhatsApp — we'll help you pick the right
              program. Or leave your details below and we'll reach out.
            </p>
            <div className="mt-7 md:mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={waLink(WA_MESSAGES.trial)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all"
              >
                <MessageCircle className="h-4 w-4" /> Book Your Free Consultation
              </a>
            </div>
            <ul className="mt-8 md:mt-10 space-y-3 text-sm">
              {PERKS.map((l) => (
                <li key={l} className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-clay-light shrink-0 mt-0.5" />{l}</li>
              ))}
            </ul>
          </div>
          <form onSubmit={onSubmit} className="p-8 md:p-14" data-testid="lead-form">
            <p className="eyebrow">Prefer email? Leave your details</p>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl text-forest leading-tight">We'll reach out within 24 hours.</h3>

            {done && <div className="mt-5 rounded-xl bg-green-50 border border-green-200 text-green-800 p-4 text-sm">Request received. We'll message you soon.</div>}

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input data-testid="form-name" required value={form.name} onChange={(e) => handleChange("name", e.target.value)} className={INPUT_CLS} placeholder="Your name" />
              </Field>
              <Field label="WhatsApp / Phone" required>
                <input data-testid="form-phone" required value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className={INPUT_CLS} placeholder="+1 555 000 0000" />
              </Field>
              <Field label="Email" required className="sm:col-span-2">
                <input data-testid="form-email" type="email" required value={form.email} onChange={(e) => handleChange("email", e.target.value)} className={INPUT_CLS} placeholder="you@email.com" />
              </Field>
              <Field label="Your goal" className="sm:col-span-2">
                <textarea data-testid="form-message" rows={3} value={form.message} onChange={(e) => handleChange("message", e.target.value)} className={INPUT_CLS} placeholder="e.g. I want to lose 8 kg and feel more energetic" />
              </Field>
            </div>

            <button data-testid="form-submit" disabled={loading} type="submit" className="btn-accent mt-6 w-full sm:w-auto">
              {loading ? "Sending…" : "Send enquiry"}
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 text-xs text-ink-muted">We'll never share your details. Unsubscribe anytime.</p>
          </form>
        </div>
      </div>
    </section>
  );
}
